import React from 'react';
import StoreIcon from '@mui/icons-material/Store';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  CircularProgress,
  Button,
} from '@mui/material';
import CompanySelectBox from './CompanySelectBox';
import {
  ROLE_NUVE_MASTER_ADMIN,
  ROLE_TECH_ADMIN,
} from '../../../settings/ManageUsers/constants';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField, InputAdornment } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import {
  ACCESS_MODES,
  VIEW_ONLY_SHORT,
} from '../../../../constants/readOnlyAccess';
import '../../ReadOnly/ReadOnly.css';
import AuthUtil from '../../../../redux/AuthUtil';

function inferGroup(company) {
  if (company.group) return company.group;
  if (company.compType) return company.compType;
  const plan = (company.planName || '').toLowerCase();
  return plan.includes('alpha') ? 'alpha' : 'global';
}

function getLabel(entry) {
  return entry.companyCode
    ? `${entry.name} (${entry.companyCode})`
    : entry.name || '';
}

function getInitials(name) {
  return (name || '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const C = {
  navy: '#0f1847',
  alpha: '#6d28d9',
  alphaBg: '#f5f3ff',
  alphaBorder: '#ede9fe',
  global: '#0369a1',
  globalBg: '#f0f9ff',
  globalBorder: '#e0f2fe',
  ndsBg: '#f8fafc',
  ndsBorder: '#e2e8f0',
  gold: '#D4AF37',
  selected: '#059669',
  selectedBg: '#ecfdf5',
  border: '#edf0f5',
  muted: '#8a9cc5',
  text: '#1e293b',
};

class CompanyPicker extends React.Component {
  state = {
    dialogOpen: false,
  };

  handleToggle = () => {
    const opening = !this.state.dialogOpen;
    this.setState({ dialogOpen: opening });

    // Opening the dialog re-asks for the list. It is otherwise fetched
    // once, in Header.componentDidMount, with no retry — so one failed
    // request at boot (a slow connection, a backend still coming up) left
    // this dialog empty for the rest of the session, and the only way to
    // switch location again was a full page reload.
    if (opening) this.refreshCompanies();
  };

  refreshCompanies = () => {
    if (this.props.refreshAvailableCompanies) {
      this.props.refreshAvailableCompanies();
    }
  };

  handleClose = () => {
    this.setState({ dialogOpen: false });
  };

  handleCompanyChange = (value) => {
    const originalAuthorities = this.props.userInfo.authority || [];
    const companyAuthorities = (value.authorities || []).map(
      (item) => item.authority || item
    );
    const originalRoutes = this.props.userInfo.routes || [];
    const companyRoutes = value.routes || [];

    let authorityArray;
    let routesArray;

    if (
      originalAuthorities.includes(ROLE_TECH_ADMIN) ||
      originalAuthorities.includes(ROLE_NUVE_MASTER_ADMIN)
    ) {
      authorityArray = originalAuthorities;
      routesArray = originalRoutes;
    } else {
      authorityArray = companyAuthorities.length
        ? companyAuthorities
        : originalAuthorities;
      routesArray = companyRoutes.length ? companyRoutes : originalRoutes;
    }

    const updatedUserInfo = {
      ...this.props.userInfo,
      routes: routesArray,
      authority: authorityArray,
      selectedLocationId: value.companyId,
      selectedLocationName:
        value.name || this.props.userInfo.selectedLocationName,
      planName: value.planName,
    };

    // Persist the selected companyId so AuthUtil can stamp it on every
    // subsequent request as the X-Selected-Company header. Without this,
    // the backend LockGateInterceptor and AccountService.getUserInfo
    // would keep evaluating the user's home company, letting a user
    // switch to a locked linked company and operate it.
    if (value.companyId) {
      localStorage.setItem('selectedCompanyId', value.companyId);
    }

    // Same reasoning for the access mode: the switch reloads the page
    // rather than re-running getUser, so the new location's mode has to
    // be stamped here or the app would spend the next page load enforcing
    // the PREVIOUS location's mode — full control at a view-only site, or
    // a greyed-out UI at a site where they actually have full control.
    // listAvailableCompanies resolves this server-side per company.
    const accessMode = value.accessMode || 'FULL';
    localStorage.setItem('accessMode', accessMode);
    updatedUserInfo.accessMode = accessMode;

    this.props.updateUserInfo(updatedUserInfo);

    // THE RELOAD MUST NOT OUTRUN REDUX-PERSIST.
    //
    // The two lines above are the two copies of "which company am I in",
    // and only one of them is written synchronously. localStorage feeds
    // the X-Selected-Company header; `userInfo.selectedLocationId` — what
    // screens put in their request BODIES — lives in the persisted auth
    // slice, which redux-persist writes on a later tick. Reloading 100ms
    // after the dispatch raced that write, and losing the race meant the
    // page came back asking the NEW location in its headers and the OLD
    // one in its bodies: one page load of a screen reading the previous
    // gameroom's numbers under the newly-picked gameroom's name.
    //
    // Flushing first means the location the user just picked is what
    // rehydrates. Same helper the 409 / revoked-access paths use.
    AuthUtil.flushPersistedState().then(() =>
      setTimeout(() => {
        window.location.reload();
      }, 100)
    );
  };

  renderNDSCard(nds, isSelected) {
    return (
      <div
        onClick={() => !isSelected && this.handleCompanyChange(nds)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          borderRadius: 10,
          cursor: isSelected ? 'default' : 'pointer',
          background: isSelected ? C.selectedBg : C.ndsBg,
          border: `1.5px solid ${isSelected ? C.selected : C.ndsBorder}`,
          transition: 'all .15s',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            flexShrink: 0,
            background: `linear-gradient(135deg, ${C.navy}, #1a2666)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.gold,
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          NDS
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>
            NuVe System
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>
            {(this.props.userInfo.authority || []).includes(ROLE_TECH_ADMIN) &&
            !(this.props.userInfo.authority || []).includes(
              ROLE_NUVE_MASTER_ADMIN
            )
              ? 'Tech Admin'
              : 'Owner'}
          </div>
        </div>
        {isSelected && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 4,
              background: C.selected,
              color: '#fff',
            }}
          >
            ACTIVE
          </div>
        )}
      </div>
    );
  }

  renderGroupSection(
    companies,
    selectedId,
    accentColor,
    bgColor,
    borderColor,
    title,
    placeholder
  ) {
    const selected = companies.find((c) => c.companyId === selectedId) || null;

    const filterFn = (opts, state) =>
      opts.filter((o) => {
        const input = state.inputValue.toLowerCase();
        return (
          o.name.toLowerCase().includes(input) ||
          (o.companyCode && o.companyCode.toLowerCase().includes(input))
        );
      });

    return (
      <div
        style={{
          background: bgColor,
          borderRadius: 12,
          padding: 14,
          border: `1px solid ${borderColor}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: accentColor,
              }}
            />
            <span style={{ fontSize: 13, fontWeight: 700, color: accentColor }}>
              {title}
            </span>
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: accentColor,
              background: `${accentColor}15`,
              padding: '2px 8px',
              borderRadius: 10,
            }}
          >
            {companies.length}
          </span>
        </div>

        <Autocomplete
          options={companies}
          getOptionLabel={getLabel}
          autoHighlight
          disableClearable={!!selected}
          onChange={(e, value) => value && this.handleCompanyChange(value)}
          blurOnSelect
          filterOptions={filterFn}
          value={selected}
          renderOption={(option) => {
            const isCurrent = option.companyId === selectedId;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '4px 0',
                }}
              >
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    flexShrink: 0,
                    background: isCurrent ? C.selected : `${accentColor}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isCurrent ? '#fff' : accentColor,
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {getInitials(option.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: isCurrent ? C.selected : C.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option.name}
                  </div>
                  {option.companyCode && (
                    <div style={{ fontSize: 11, color: C.muted }}>
                      {option.companyCode}
                    </div>
                  )}
                </div>
                {/* Access level for this user at this location. Shown
                    BEFORE they switch so "why can't I edit anything?"
                    never comes up — they picked the view-only site
                    knowing it was view-only. */}
                {option.accessMode === ACCESS_MODES.VIEW && (
                  <span className="nuve-readonly-badge">{VIEW_ONLY_SHORT}</span>
                )}
                {isCurrent && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={C.selected}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 18, color: C.muted }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
                style: { fontSize: 13, background: '#fff', borderRadius: 8 },
              }}
            />
          )}
        />
      </div>
    );
  }

  renderGroupedPicker() {
    const allCompanies = this.props.availableCompanies || [];
    const selectedId = this.props.userInfo.selectedLocationId;

    const alphaCompanies = allCompanies.filter(
      (c) => c.name !== 'NuVe System' && inferGroup(c) === 'alpha'
    );
    const globalCompanies = allCompanies.filter(
      (c) => c.name !== 'NuVe System' && inferGroup(c) === 'global'
    );
    const nds = allCompanies.find((c) => c.name === 'NuVe System');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {nds && this.renderNDSCard(nds, nds.companyId === selectedId)}

        {alphaCompanies.length > 0 &&
          this.renderGroupSection(
            alphaCompanies,
            selectedId,
            C.alpha,
            C.alphaBg,
            C.alphaBorder,
            'Alpha',
            'Search alpha locations...'
          )}

        {globalCompanies.length > 0 &&
          this.renderGroupSection(
            globalCompanies,
            selectedId,
            C.global,
            C.globalBg,
            C.globalBorder,
            'Global',
            'Search global locations...'
          )}
      </div>
    );
  }

  // What to show when we have no companies to offer. An empty list is
  // ambiguous on its own — it looks identical whether the fetch failed or
  // the account genuinely has one location — so say which, and give the
  // failed case a way out that isn't reloading the page.
  renderNoCompanies() {
    const status = this.props.availableCompaniesStatus;

    if (status === 'loading') {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '24px 8px',
            color: C.muted,
            fontSize: 13,
          }}
        >
          <CircularProgress size={18} style={{ color: C.navy }} />
          Loading your locations...
        </div>
      );
    }

    // Settled with nothing in it. Either the fetch failed, or this account
    // genuinely reaches only the location it is already in — say which,
    // and let them ask again either way rather than stare at a blank box.
    const failed = status === 'failed';

    return (
      <div style={{ padding: '16px 8px', textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>
          {failed ? "Couldn't load your locations" : 'No other locations'}
        </div>
        <div style={{ fontSize: 12, color: C.muted, margin: '6px 0 14px' }}>
          {failed
            ? "The list didn't come through. Your session is fine — try again."
            : 'Nothing else is linked to this account yet.'}
        </div>
        <Button
          variant="contained"
          size="small"
          onClick={this.refreshCompanies}
          style={{ background: C.navy, color: '#fff', textTransform: 'none' }}
        >
          Try again
        </Button>
      </div>
    );
  }

  render() {
    const userInfo = this.props.userInfo || {};
    const authorities = userInfo.authority || [];
    // Owners carry every role including ROLE_TECH_ADMIN, so only
    // treat non-owners as real tech admins here.
    const isTechAdmin =
      authorities.includes(ROLE_TECH_ADMIN) &&
      !authorities.includes(ROLE_NUVE_MASTER_ADMIN);
    const companyIds = isTechAdmin
      ? userInfo.selectedLocations || []
      : userInfo.companyIds && userInfo.companyIds.length > 0
      ? userInfo.companyIds
      : userInfo.selectedLocations || [];
    const { dialogOpen } = this.state;
    if (companyIds.length === 0) return null;

    const showGrouped = (this.props.availableCompanies || []).length > 0;
    // The fallback CompanySelectBox renders from the same fetched list, so
    // when it is empty that picker has nothing in it either. Show why.
    const hasNoCompanies = !showGrouped;

    return (
      <>
        <div>
          <Tooltip title="Select Company" arrow>
            <IconButton size="large" onClick={this.handleToggle}>
              <StoreIcon
                style={{ fontSize: '30px', color: 'rgb(120, 150, 255)' }}
              />
            </IconButton>
          </Tooltip>
        </div>

        <Dialog
          open={dialogOpen}
          onClose={this.handleClose}
          maxWidth="xs"
          fullWidth
          disableScrollLock={false}
          scroll="paper"
          style={{ alignItems: 'flex-start' }}
          PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
        >
          {showGrouped ? (
            <DialogContent style={{ padding: 16 }}>
              {this.renderGroupedPicker()}
            </DialogContent>
          ) : hasNoCompanies ? (
            <>
              <div
                style={{
                  padding: '16px 16px 0',
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.navy,
                }}
              >
                Select Company
              </div>
              <DialogContent>{this.renderNoCompanies()}</DialogContent>
            </>
          ) : (
            <>
              <div
                style={{
                  padding: '16px 16px 0',
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.navy,
                }}
              >
                Select Company
              </div>
              <DialogContent>
                <CompanySelectBox
                  userInfo={this.props.userInfo}
                  selectedLocations={companyIds}
                  updateCallback={this.handleCompanyChange}
                />
              </DialogContent>
            </>
          )}
        </Dialog>
      </>
    );
  }
}

export default CompanyPicker;
